import React, { useState, useEffect, useContext, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

// MUI Core
import {
    Box,
    Grid,
    Paper,
    Typography,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Button,
    IconButton,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TableSortLabel,
    Card,
    CardHeader,
    CardContent,
    Chip,
    Tooltip,
    Avatar,
    Pagination,
    Stack,
    Fade,
    Skeleton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    List,
    ListItemButton,
    ListItemText,
    Radio,
    CircularProgress,
    useMediaQuery,
    ThemeProvider,
    createTheme,
} from "@mui/material";
import { styled } from "@mui/material/styles";

// MUI Icons
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import ViewListIcon from "@mui/icons-material/ViewList";
import DownloadIcon from "@mui/icons-material/Download";
import SearchIcon from "@mui/icons-material/Search";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import VerifiedIcon from "@mui/icons-material/Verified";
import ReceiptIcon from "@mui/icons-material/Receipt";
import PaymentIcon from "@mui/icons-material/Payment";
import SendIcon from "@mui/icons-material/Send";
import FlagIcon from "@mui/icons-material/Flag";
import ScheduleIcon from "@mui/icons-material/Schedule";
import SchoolIcon from "@mui/icons-material/School";
import QuizIcon from "@mui/icons-material/Quiz";
import EventIcon from "@mui/icons-material/Event";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import FilterListIcon from "@mui/icons-material/FilterList";
import InboxIcon from "@mui/icons-material/Inbox";

// Contexts & Utilities
import { toast } from "react-toastify";
import { UserContext } from "../Contexts/LoginContext";
import { EnquiryContext } from "../Contexts/EnquiryContext";
import { RoleContext } from "../Contexts/RoleContext";
import { fetchWithAuth } from "../Components/FetchWithAuth";
import { url } from "../Components/url";
import { fetchAcademics, setApplicationSubmittedForVisa } from "../Components/enquiryProfileApi";
import ApplicationFormModal from "../Components/ApplicationForm/ApplicationFormModal";
import { useDebouncedValue } from "../hooks/useDebouncedValue";
import { useListViewMode } from "../hooks/useListPreferences";
import { GlobalSearchBar } from "../Components/ERPList";
import { useTheme } from "../hooks/useTheme";
import { createMuiThemeFromMode } from "../theme/theme";

// Styles
import styles from "./AllEnquiries.module.scss";

const LIST_ID = "enquiries";
const PAGE_SIZE_OPTIONS = [10, 12, 25, 50, 100];

const baseThemeOptions = {
    palette: {
        secondary: { main: "#f5365c" },
        success: { main: "#2dce89" },
        warning: { main: "#fb6340" },
        info: { main: "#11cdef" },
    },
    typography: {
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
        h4: { fontWeight: 600, letterSpacing: "-0.02em" },
        button: { textTransform: "none", fontWeight: 500 },
    },
    shape: { borderRadius: 12 },
    components: {
        MuiButton: {
            styleOverrides: {
                root: { boxShadow: "none" },
            },
        },
        MuiPaper: {
            styleOverrides: {
                root: { backgroundImage: "none" },
            },
        },
    },
};

// Styled components
const StyledCard = styled(Card)(({ theme }) => ({
    cursor: "pointer",
    transition: "transform 0.2s, box-shadow 0.2s",
    "&:hover": {
        transform: "translateY(-4px)",
        boxShadow: theme.shadows[10],
    },
}));

const FilterPaper = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(2),
    marginBottom: theme.spacing(3),
    backgroundColor: "#ffffff",
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
}));

function AllEnquiries() {
    const { themeMode } = useTheme();
    const theme = useMemo(
        () => createTheme(createMuiThemeFromMode(themeMode, baseThemeOptions)),
        [themeMode]
    );
    const { username } = useContext(UserContext);
    const { role } = useContext(RoleContext);
    const { setEnquiry } = useContext(EnquiryContext);
    const navigate = useNavigate();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));

    // State
    const [enquiryData, setEnquiryData] = useState([]);
    const [exportLoading, setExportLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(12);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [viewMode, setViewMode] = useListViewMode(LIST_ID, "table");
    const [globalSearch, setGlobalSearch] = useState("");
    const debouncedGlobalSearch = useDebouncedValue(globalSearch, 400);
    const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
    const [statusOptions, setStatusOptions] = useState([]);
    const [filterOpen, setFilterOpen] = useState(!isMobile);
    const [buttonLoading, setButtonLoading] = useState(false);
    const [searchParams, setSearchParams] = useState({
        name: "",
        source: "",
        status: "",
        nic: "",
        email: "",
        phone_number: "",
        address: "",
        gender: "",
        enquiryId: "",
        startDate: "",
        endDate: "",
    });
    const debouncedSearchParams = useDebouncedValue(searchParams, 400);
    /** After Reset we already fetch with cleared params; skip the debounce-driven effect once to avoid a duplicate API call. */
    const skipNextDebounceFetchRef = useRef(false);
    const [submitVisaModalOpen, setSubmitVisaModalOpen] = useState(false);
    const [submitVisaEnquiry, setSubmitVisaEnquiry] = useState(null);
    const [submitVisaApplications, setSubmitVisaApplications] = useState([]);
    const [submitVisaSelectedId, setSubmitVisaSelectedId] = useState(null);
    const [submitVisaLoading, setSubmitVisaLoading] = useState(false);
    const [submitVisaFetching, setSubmitVisaFetching] = useState(false);
    const [applicationFormModalOpen, setApplicationFormModalOpen] = useState(false);
    const [applicationFormEnquiry, setApplicationFormEnquiry] = useState(null);
    const [contactedModalOpen, setContactedModalOpen] = useState(false);
    const [contactedModalEnquiry, setContactedModalEnquiry] = useState(null);
    const [contactLeadStatus, setContactLeadStatus] = useState("");
    const [contactCommentText, setContactCommentText] = useState("");
    const [contactSubmitLoading, setContactSubmitLoading] = useState(false);
    const [contactError, setContactError] = useState(null);

    const showAssignedCounsellorColumn = useMemo(
        () => role === "admin" || role === "Manager" || role === "Receptionist",
        [role]
    );

    // ---------- Helper Functions ----------
    const calculateAge = (dateString) => {
        if (!dateString) return "N/A";
        const today = new Date();
        const birthDate = new Date(dateString);
        let age = today.getFullYear() - birthDate.getFullYear();
        const m = today.getMonth() - birthDate.getMonth();
        if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) age--;
        return age;
    };

    const getStatusColor = (status) => {
        switch (status) {
            case "New Enquiry":
                return "warning";
            case "Contacted":
                return "info";
            case "Application Form Filled":
                return "secondary";
            case "Future Lead":
                return "default";
            case "Pending Invoice Payment":
            case "Pending fee payment":
                return "warning";
            case "Pending COE":
                return "primary";
            case "Pending Visa Submission":
                return "info";
            case "Pending Visa Decision":
                return "warning";
            case "Study Start":
                return "success";
            case "Visa Rejected":
                return "error";
            case "Visa Approved":
                return "success";
            case "Backend processing":
                return "default";
            case "Sitting class":
                return "primary";
            case "Awaiting exam":
            case "Sitting awaiting exam":
                return "info";
            case "Awaiting seminar":
            case "Sitting awaiting seminar":
                return "secondary";
            case "Sitting awaiting class":
                return "primary";
            case "Class completed":
            case "Exam completed":
            case "Seminar completed":
                return "success";
            default:
                return "default";
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case "New Enquiry":
                return <FileUploadIcon fontSize="small" />;
            case "Contacted":
                return <VerifiedIcon fontSize="small" />;
            case "Application Form Filled":
                return <ReceiptIcon fontSize="small" />;
            case "Future Lead":
                return <ScheduleIcon fontSize="small" />;
            case "Pending Invoice Payment":
            case "Pending fee payment":
                return <PaymentIcon fontSize="small" />;
            case "Pending COE":
                return <FlagIcon fontSize="small" />;
            case "Pending Visa Submission":
                return <SendIcon fontSize="small" />;
            case "Pending Visa Decision":
                return <CheckCircleIcon fontSize="small" />;
            case "Study Start":
                return <CheckCircleIcon fontSize="small" />;
            case "Visa Rejected":
                return <CancelIcon fontSize="small" />;
            case "Visa Approved":
                return <CheckCircleIcon fontSize="small" />;
            case "Backend processing":
                return <HourglassEmptyIcon fontSize="small" />;
            case "Sitting class":
            case "Sitting awaiting class":
                return <SchoolIcon fontSize="small" />;
            case "Awaiting exam":
            case "Sitting awaiting exam":
                return <QuizIcon fontSize="small" />;
            case "Awaiting seminar":
            case "Sitting awaiting seminar":
                return <EventIcon fontSize="small" />;
            case "Class completed":
            case "Exam completed":
            case "Seminar completed":
                return <CheckCircleIcon fontSize="small" />;
            default:
                return null;
        }
    };

    // ---------- Data Fetching Functions ----------
    const getStatuses = async () => {
        try {
            const response = await fetchWithAuth(`${url}/status/all`, {
                method: "GET",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
            });
            const result = await response.json();
            if (response.ok) setStatusOptions(result);
            else toast.error(result.error || "Error fetching status options.");
        } catch (error) {
            toast.error("Error fetching status options.");
        }
    };

    const get_filtered_enquiries = useCallback(
        async (page = 1, overrideParams = null, signal = null) => {
            setLoading(true);
            try {
                const params = overrideParams !== null ? overrideParams : debouncedSearchParams;
                const queryParams = new URLSearchParams({
                    id: username,
                    page,
                    page_size: pageSize,
                    status: params.status || "none",
                    name: params.name || "none",
                    source: params.source || "none",
                    nic: params.nic || "none",
                    email: params.email || "none",
                    phone_number: params.phone_number || "none",
                    address: params.address || "none",
                    gender: params.gender || "none",
                    enquiryId: params.enquiryId || "none",
                    start_date: params.startDate || "none",
                    end_date: params.endDate || "none",
                }).toString();

                const response = await fetchWithAuth(
                    `${url}/enquiry/get_all_enquiries?${queryParams}`,
                    {
                        method: "GET",
                        headers: { "Content-Type": "application/json" },
                        credentials: "include",
                        signal,
                        retry: true,
                    }
                );
                if (signal?.aborted) return;
                const result = await response.json();
                if (signal?.aborted) return;

                if (response.ok) {
                    setEnquiryData(result.data || []);
                    setTotalPages(Math.ceil((result.total || 0) / pageSize));
                    setTotalCount(result.total || 0);
                    setCurrentPage(page);
                    setErrorMessage("");
                } else {
                    const msg = result.error || "Error loading enquiries";
                    setErrorMessage(msg);
                    toast.error(msg);
                }
            } catch (error) {
                if (error?.name === "AbortError") return;
                const msg = "An error occurred. Please try again.";
                setErrorMessage(msg);
                toast.error(msg);
            } finally {
                if (!signal?.aborted) setLoading(false);
            }
        },
        [username, pageSize, debouncedSearchParams]
    );

    const downloadExcel = async () => {
        setExportLoading(true);
        try {
            const queryParams = new URLSearchParams({
                page_size: "10000",
                status: searchParams.status || "none",
                name: searchParams.name || "none",
                source: searchParams.source || "none",
                nic: searchParams.nic || "none",
                email: searchParams.email || "none",
                phone_number: searchParams.phone_number || "none",
                address: searchParams.address || "none",
                gender: searchParams.gender || "none",
                enquiryId: searchParams.enquiryId || "none",
                start_date: searchParams.startDate || "none",
                end_date: searchParams.endDate || "none",
            }).toString();

            const response = await fetchWithAuth(
                `${url}/enquiry/export_enquiries?${queryParams}`,
                {
                    method: "GET",
                    headers: { "Content-Type": "application/json" },
                    credentials: "include",
                }
            );
            const result = await response.json();

            if (response.status === 404) {
                toast.error(
                    "Export is not available on the API yet. Deploy the latest backend (export_enquiries) to Railway, then try again."
                );
                return;
            }
            if (!response.ok) {
                toast.error(result.error || result.message || "Export failed.");
                return;
            }

            const columns = result.columns || [];
            const rows = result.rows || [];
            if (!rows.length) {
                toast.warning("No enquiries match the current filters.");
                return;
            }

            const worksheet = XLSX.utils.json_to_sheet(rows, {
                header: columns.length ? columns : undefined,
            });
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, "Enquiries");
            const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
            const blob = new Blob([excelBuffer], {
                type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            });
            saveAs(blob, "Enquiries.xlsx");
            toast.success("Export downloaded successfully.");
        } catch (error) {
            toast.error("An error occurred while exporting.");
        } finally {
            setExportLoading(false);
        }
    };

    // ---------- Effects ----------
    useEffect(() => {
        if (role === "admin" || role === "Manager" || role === "Receptionist") {
            getStatuses();
        }
    }, []);

    useEffect(() => {
        if (skipNextDebounceFetchRef.current) {
            skipNextDebounceFetchRef.current = false;
            return;
        }
        const controller = new AbortController();
        setCurrentPage(1);
        get_filtered_enquiries(1, null, controller.signal);
        return () => controller.abort();
    }, [get_filtered_enquiries]);

    // ---------- Event Handlers ----------
    const handleInputChange = (e) => {
        setSearchParams({ ...searchParams, [e.target.name]: e.target.value });
    };


    const openSubmitVisaModal = async (enquiry) => {
        setSubmitVisaEnquiry(enquiry);
        setSubmitVisaSelectedId(null);
        setSubmitVisaModalOpen(true);
        setSubmitVisaFetching(true);
        setSubmitVisaApplications([]);
        try {
            const data = await fetchAcademics(enquiry.id);
            const apps = Array.isArray(data?.course_preference) ? data.course_preference : [];
            setSubmitVisaApplications(apps);
            if (apps.length === 1) setSubmitVisaSelectedId(apps[0].id);
        } catch (err) {
            toast.error(err?.message || "Failed to load applications.");
        } finally {
            setSubmitVisaFetching(false);
        }
    };

    const closeSubmitVisaModal = () => {
        setSubmitVisaModalOpen(false);
        setSubmitVisaEnquiry(null);
        setSubmitVisaApplications([]);
        setSubmitVisaSelectedId(null);
    };

    const handleSubmitVisaSave = async () => {
        if (!submitVisaEnquiry || !submitVisaSelectedId) {
            toast.warning("Please select an application.");
            return;
        }
        setSubmitVisaLoading(true);
        try {
            await setApplicationSubmittedForVisa(submitVisaSelectedId);
            await change_status(submitVisaEnquiry.id, "Pending Visa Submission", username);
            closeSubmitVisaModal();
            get_filtered_enquiries(currentPage);
        } catch (err) {
            toast.error(err?.message || "Failed to save.");
        } finally {
            setSubmitVisaLoading(false);
        }
    };

    const resetFilters = () => {
        setGlobalSearch("");
        const cleared = {
            name: "", source: "", status: "", nic: "", email: "",
            phone_number: "", address: "", gender: "", enquiryId: "", startDate: "", endDate: "",
        };
        setSearchParams(cleared);
        skipNextDebounceFetchRef.current = true;
        get_filtered_enquiries(1, cleared);
        // If filters were already empty, debouncedSearchParams may not change and the effect never clears the skip.
        window.setTimeout(() => {
            skipNextDebounceFetchRef.current = false;
        }, 500);
        toast.info("Filters cleared.");
    };

    const change_status = async (enquiry_id, status, username) => {
        try {

            setButtonLoading(true);
            const response = await fetchWithAuth(`${url}/update_status`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    enquiry_id: enquiry_id,
                    status,
                    assigned_by: username,
                }),
                credentials: "include",
            });
            if (response.ok) {
                toast.success("Status updated successfully.");
                setButtonLoading(false);
                get_filtered_enquiries(currentPage);
            } else {
                const data = await response.json();
                setButtonLoading(false);
                toast.error(data.error || "Failed to update status.");
            }
        } catch (error) {
            setButtonLoading(false);
            toast.error("An error occurred while updating status.");
        }
    };

    const openContactedModal = (enquiry) => {
        setContactedModalEnquiry(enquiry);
        setContactLeadStatus("");
        setContactCommentText("");
        setContactError(null);
        setContactedModalOpen(true);
    };

    const closeContactedModal = () => {
        if (!contactSubmitLoading) {
            setContactedModalOpen(false);
            setContactedModalEnquiry(null);
            setContactLeadStatus("");
            setContactCommentText("");
            setContactError(null);
        }
    };

    const handleContactedSubmit = async (e) => {
        e.preventDefault();
        const leadStatus = (contactLeadStatus || "").trim();
        const text = (contactCommentText || "").trim();
        if (!leadStatus || !text) {
            setContactError("Please select lead status (Hot/Warm/Cold) and enter a comment.");
            return;
        }
        setContactSubmitLoading(true);
        setContactError(null);
        try {
            const fullText = `${leadStatus} - ${text}`;
            const commentPayload = {
                c_made_by: username,
                made_for: contactedModalEnquiry.id,
                text: fullText,
            };
            const comRes = await fetchWithAuth(`${url}/enquiry/communication`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(commentPayload),
                credentials: "include",
            });
            if (!comRes.ok) {
                const errData = await comRes.json().catch(() => ({}));
                setContactError(errData.error || "Failed to add comment.");
                setContactSubmitLoading(false);
                return;
            }
            await change_status(contactedModalEnquiry.id, "Contacted", username);
            closeContactedModal();
        } catch (err) {
            setContactError("An error occurred. Please try again.");
        } finally {
            setContactSubmitLoading(false);
        }
    };

    const handleViewEnquiry = (enquiry) => {
        const dataDictionary = Array.isArray(enquiry)
            ? enquiry.reduce((acc, item, index) => {
                acc[index] = item;
                return acc;
            }, {})
            : enquiry;
        setEnquiry(dataDictionary);
        navigate("/enquiry_profile", { state: { enquiry } });
    };
    const handleNavigateToAllocateProgram = (enquiry) => {
        navigate("/enquiry_profile", { state: { enquiry, openTab: "Courses" } });
    };
    const toggleViewMode = () => {
        setViewMode((prev) => (prev === "cards" ? "table" : "cards"));
    };

    // Client-side global search (name, ID, email, phone)
    const searchLower = (debouncedGlobalSearch || "").trim().toLowerCase();
    const filteredBySearch = searchLower
        ? enquiryData.filter((e) => {
            const name = `${e.first_name || ""} ${e.last_name || ""}`.toLowerCase();
            const id = String(e.id || "");
            const email = (e.email || "").toLowerCase();
            const phone = String(e.phone_number || "");
            const counsellor = (e.assigned_counsellor_name || "").toLowerCase();
            return (
                name.includes(searchLower) ||
                id.includes(searchLower) ||
                email.includes(searchLower) ||
                phone.includes(searchLower) ||
                counsellor.includes(searchLower)
            );
        })
        : enquiryData;

    // Client-side sorting
    const requestSort = (key) => {
        let direction = "asc";
        if (sortConfig.key === key && sortConfig.direction === "asc") {
            direction = "desc";
        }
        setSortConfig({ key, direction });
    };
    const getSortedData = () => {
        if (!sortConfig.key) return filteredBySearch;
        return [...filteredBySearch].sort((a, b) => {
            let aVal = a[sortConfig.key];
            let bVal = b[sortConfig.key];
            if (sortConfig.key === "age" || (sortConfig.key === "date_of_birth" && aVal && bVal)) {
                aVal = aVal ? new Date(aVal).getTime() : 0;
                bVal = bVal ? new Date(bVal).getTime() : 0;
                return sortConfig.direction === "asc" ? aVal - bVal : bVal - aVal;
            }
            if (aVal == null) aVal = "";
            if (bVal == null) bVal = "";
            if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
            if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
            return 0;
        });
    };
    const displayData = getSortedData();
    const savingButton = (text) => buttonLoading ? <p> Saving</p> : text;

    // Render action buttons based on status
    const renderActionButtons = (enquiry) => {
        if (role !== "admin") return null;
        const buttons = [];

        switch (enquiry.status) {
            case "New Enquiry":
                buttons.push(
                    <Tooltip title="Mark as Contacted (add first comment and lead status)" key="docs">
                        <Button
                            variant="outlined"
                            size="small"
                            disabled={buttonLoading}
                            startIcon={<FileUploadIcon />}
                            onClick={(e) => {
                                e.stopPropagation();
                                openContactedModal(enquiry);
                            }}
                        >
                            {savingButton("Contacted")}
                        </Button>
                    </Tooltip>
                );
                break;
            case "Contacted":
                buttons.push(
                    <Tooltip title="Move to Application Form Filled" key="verify">
                        <Button
                            variant="outlined"
                            size="small"
                            disabled={buttonLoading}
                            startIcon={<VerifiedIcon />}
                            onClick={(e) => {
                                e.stopPropagation();
                                setApplicationFormEnquiry(enquiry);
                                setApplicationFormModalOpen(true);
                            }}
                        >
                            Move to Application Form Filled
                        </Button>
                    </Tooltip>,
                    <Tooltip title="Future Lead" key="future">
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<PersonAddIcon />}
                            disabled={buttonLoading}
                            onClick={(e) => {
                                e.stopPropagation();
                                change_status(enquiry.id, "Future Lead", username);
                            }}
                        >
                            {savingButton("Future Lead")}
                        </Button>
                    </Tooltip>
                );
                break;
            case "Future Lead":
                buttons.push(
                    <Tooltip title="Move to Application Form Filled" key="verify">
                        <Button
                            variant="outlined"
                            size="small"
                            disabled={buttonLoading}
                            startIcon={<VerifiedIcon />}
                            onClick={(e) => {
                                e.stopPropagation();
                                setApplicationFormEnquiry(enquiry);
                                setApplicationFormModalOpen(true);
                            }}
                        >
                            Move to Application Form Filled
                        </Button>
                    </Tooltip>
                );
                break;
            case "Application Form Filled":
                buttons.push(
                    <Tooltip title="Invoice Raised" key="invoice">
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<ReceiptIcon />}
                            disabled={buttonLoading}
                            onClick={(e) => {
                                e.stopPropagation();
                                handleNavigateToAllocateProgram(enquiry);
                            }}
                        >
                            {savingButton("Allocate Program")}
                        </Button>
                    </Tooltip>
                );
                break;
            case "Pending Invoice Payment":
                buttons.push(
                    <Tooltip title="Invoice Paid" key="paid">
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<PaymentIcon />}
                            disabled={buttonLoading}
                            onClick={(e) => {
                                e.stopPropagation();
                                change_status(enquiry.id, "Pending COE", username);
                            }}
                        >
                            {savingButton("Payment")}
                        </Button>
                    </Tooltip>
                );
                break;
            case "Pending COE":
                buttons.push(
                    <Tooltip title="COE Received" key="coe">
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<FlagIcon />}
                            disabled={buttonLoading}
                            onClick={(e) => {
                                e.stopPropagation();
                                openSubmitVisaModal(enquiry);
                            }}
                        >
                            {savingButton("COE")}
                        </Button>
                    </Tooltip>
                );
                break;
            case "Pending Visa Submission":
                buttons.push(
                    <Tooltip title="Select application and mark as submitted for visa" key="submit">
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<SendIcon />}
                            disabled={buttonLoading}
                            onClick={(e) => {
                                e.stopPropagation();
                                change_status(enquiry.id, "Pending Visa Decision", username);
                            }}
                        >
                            {savingButton("Submit")}
                        </Button>
                    </Tooltip>
                );
                break;
            case "Pending Visa Decision":
                buttons.push(
                    <Tooltip title="Approve" key="approve">
                        <Button
                            variant="contained"
                            color="success"
                            size="small"
                            startIcon={<CheckCircleIcon />}
                            disabled={buttonLoading}
                            onClick={(e) => {
                                e.stopPropagation();
                                change_status(enquiry.id, "Study Start", username);
                            }}
                            sx={{ mr: 1 }}
                        >
                            {savingButton("Approve")}
                        </Button>
                    </Tooltip>,
                    <Tooltip title="Reject" key="reject">
                        <Button
                            variant="contained"
                            color="error"
                            size="small"
                            startIcon={<CancelIcon />}
                            disabled={buttonLoading}
                            onClick={(e) => {
                                e.stopPropagation();
                                change_status(enquiry.id, "Visa Rejected", username);
                            }}
                        >
                            {savingButton("Reject")}
                        </Button>
                    </Tooltip>
                );
                break;
            case "Study Start":
                buttons.push(
                    <Tooltip title="Attending" key="attend">
                        <Button
                            variant="contained"
                            color="success"
                            size="small"
                            startIcon={<CheckCircleIcon />}
                            disabled={buttonLoading}
                            onClick={(e) => {
                                e.stopPropagation();
                                change_status(enquiry.id, "Visa Approved", username);
                            }}
                            sx={{ mr: 1 }}
                        >
                            {savingButton("Attending")}
                        </Button>
                    </Tooltip>,
                    <Tooltip title="Not Attending" key="notAttend">
                        <Button
                            variant="contained"
                            color="warning"
                            size="small"
                            startIcon={<CancelIcon />}
                            disabled={buttonLoading}
                            onClick={(e) => {
                                e.stopPropagation();
                                change_status(enquiry.id, "Visa Approved", username);
                            }}
                        >
                            {savingButton("Not Attending")}
                        </Button>
                    </Tooltip>
                );
                break;
            default:
                break;
        }
        return <Stack direction="row" spacing={1} className={styles.actionButtons}>{buttons}</Stack>;
    };

    // ---------- JSX ----------
    return (
        <ThemeProvider theme={theme}>
            <Box className={styles.root}>
                <Paper elevation={0} className={styles.contentPaper}>
                            {/* Title only on first row */}
                            <Box sx={{ marginBottom: 2 }}>
                                <Stack direction="row" alignItems="center" spacing={2}>
                                    <Box>
                                        <Typography variant="h4" className={styles.pageTitle}>
                                            Enquiries
                                        </Typography>
                                        <Typography variant="body2" className={styles.pageSubtitle} sx={{ mt: 0.5 }}>
                                            View and manage all student enquiries.
                                        </Typography>
                                    </Box>
                                </Stack>
                            </Box>

                            {/* Search, filters toggle, view toggle, and actions below title */}
                            <Box className={styles.header}>
                                <GlobalSearchBar
                                    value={globalSearch}
                                    onChange={setGlobalSearch}
                                    placeholder="Search name, ID, email, phone…"
                                />
                                <Box className={styles.headerActions}>
                                    <Button
                                        startIcon={<FilterListIcon />}
                                        onClick={() => setFilterOpen(!filterOpen)}
                                        variant="outlined"
                                        size="small"
                                    >
                                        {filterOpen ? "Hide Filters" : "Show Filters"}
                                    </Button>
                                    <Tooltip title={viewMode === "cards" ? "Table View" : "Card View"}>
                                        <IconButton onClick={toggleViewMode} color="primary">
                                            {viewMode === "cards" ? <ViewListIcon /> : <ViewModuleIcon />}
                                        </IconButton>
                                    </Tooltip>
                                    {role === "admin" && (
                                        <Button
                                            variant="contained"
                                            startIcon={exportLoading ? <CircularProgress size={18} color="inherit" /> : <DownloadIcon />}
                                            onClick={downloadExcel}
                                            disabled={exportLoading}
                                        >
                                            {exportLoading ? "Exporting…" : "Export"}
                                        </Button>
                                    )}
                                    {(role === "admin" || role === "Manager" || role === "Receptionist") && (
                                        <Button
                                            variant="contained"
                                            startIcon={<PersonAddIcon />}
                                            onClick={() => navigate("/create-enquiry")}
                                        >
                                            New Enquiry
                                        </Button>
                                    )}

                                </Box>
                            </Box>


                            {/* Filters (collapsible) */}
                            {filterOpen ? (
                                <Fade in={filterOpen}>
                                    <FilterPaper variant="outlined">
                                        <Grid container spacing={2}>
                                            <Grid item xs={12} sm={6} md={3}>
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    label="Enquiry ID"
                                                    name="enquiryId"
                                                    value={searchParams.enquiryId}
                                                    onChange={handleInputChange}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6} md={3}>
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    label="Source"
                                                    name="source"
                                                    value={searchParams.source}
                                                    onChange={handleInputChange}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6} md={3}>
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    label="Student Name"
                                                    name="name"
                                                    value={searchParams.name}
                                                    onChange={handleInputChange}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6} md={3}>
                                                {(role === "admin" || role === "Manager" || role === "Receptionist") && (
                                                <FormControl fullWidth size="small">
                                                    <InputLabel>Status</InputLabel>
                                                    <Select
                                                        name="status"
                                                        value={searchParams.status}
                                                        label="Status"
                                                        onChange={handleInputChange}
                                                    >
                                                        <MenuItem value="">All</MenuItem>
                                                        {statusOptions.map((status, idx) => (
                                                            <MenuItem key={idx} value={status}>
                                                                {status}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>)}
                                            </Grid>
                                            <Grid item xs={12} sm={6} md={3}>
                                                <FormControl fullWidth size="small">
                                                    <InputLabel>Gender</InputLabel>
                                                    <Select
                                                        name="gender"
                                                        value={searchParams.gender}
                                                        label="Gender"
                                                        onChange={handleInputChange}
                                                    >
                                                        <MenuItem value="">All</MenuItem>
                                                        <MenuItem value="Male">Male</MenuItem>
                                                        <MenuItem value="Female">Female</MenuItem>
                                                    </Select>
                                                </FormControl>
                                            </Grid>
                                            <Grid item xs={12} sm={6} md={3}>
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    label="NIC"
                                                    name="nic"
                                                    value={searchParams.nic}
                                                    onChange={handleInputChange}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6} md={3}>
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    label="Email"
                                                    name="email"
                                                    value={searchParams.email}
                                                    onChange={handleInputChange}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6} md={3}>
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    label="Phone Number"
                                                    name="phone_number"
                                                    value={searchParams.phone_number}
                                                    onChange={handleInputChange}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6} md={3}>
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    label="Address"
                                                    name="address"
                                                    value={searchParams.address}
                                                    onChange={handleInputChange}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6} md={3}>
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    label="Start Date"
                                                    type="date"
                                                    name="startDate"
                                                    value={searchParams.startDate}
                                                    onChange={handleInputChange}
                                                    InputLabelProps={{ shrink: true }}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6} md={3}>
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    label="End Date"
                                                    type="date"
                                                    name="endDate"
                                                    value={searchParams.endDate}
                                                    onChange={handleInputChange}
                                                    InputLabelProps={{ shrink: true }}
                                                />
                                            </Grid>
                                            <Grid item xs={12} className={styles.filterActions}>
                                                <Button
                                                    variant="contained"
                                                    startIcon={<SearchIcon />}
                                                    onClick={() => get_filtered_enquiries(1, searchParams)}
                                                >
                                                    Search
                                                </Button>
                                                <Button
                                                    variant="outlined"
                                                    startIcon={<RestartAltIcon />}
                                                    onClick={resetFilters}
                                                >
                                                    Reset
                                                </Button>
                                            </Grid>
                                        </Grid>
                                    </FilterPaper>
                                </Fade>
                            ) : (null)}

                            {/* Loading Skeletons */}
                            {loading && (
                                <Box className={styles.loadingContainer}>
                                    {viewMode === "cards" ? (
                                        <Grid container spacing={2}>
                                            {[...Array(6)].map((_, i) => (
                                                <Grid item xs={12} sm={6} md={4} key={i}>
                                                    <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 2 }} />
                                                </Grid>
                                            ))}
                                        </Grid>
                                    ) : (
                                        <Box>
                                            <Skeleton variant="rectangular" height={60} sx={{ mb: 1 }} />
                                            <Skeleton variant="rectangular" height={400} />
                                        </Box>
                                    )}
                                </Box>
                            )}

                            {/* Error Message */}
                            {errorMessage && (
                                <Typography color="error" align="center" sx={{ my: 4 }}>
                                    {errorMessage}
                                </Typography>
                            )}

                            {/* Empty State */}
                            {!loading && !errorMessage && displayData.length === 0 && (
                                <Box className={styles.emptyState}>
                                    <InboxIcon sx={{ fontSize: 80, color: "text.secondary", mb: 2 }} />
                                    <Typography variant="h6" color="text.secondary">
                                        No enquiries found
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Try adjusting your filters or create a new enquiry.
                                    </Typography>
                                </Box>
                            )}

                            {/* Content: Cards or Table */}
                            {!loading && !errorMessage && displayData.length > 0 && (
                                <>
                                    {/* Pagination and rows-per-page at top */}
                                    <Box className={styles.pagination}>
                                        <Typography variant="body2" color="text.secondary">
                                            Showing {totalCount === 0 ? 0 : (currentPage - 1) * pageSize + 1}–{Math.min(currentPage * pageSize, totalCount)} of {totalCount} results
                                        </Typography>
                                        <Stack direction="row" alignItems="center" spacing={2}>
                                            <FormControl size="small" sx={{ minWidth: 100 }}>
                                                <InputLabel>Per page</InputLabel>
                                                <Select
                                                    value={pageSize}
                                                    label="Per page"
                                                    onChange={(e) => {
                                                        setPageSize(Number(e.target.value));
                                                        setCurrentPage(1);
                                                    }}
                                                >
                                                    {PAGE_SIZE_OPTIONS.map((n) => (
                                                        <MenuItem key={n} value={n}>{n}</MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                            <Pagination
                                                count={totalPages}
                                                page={currentPage}
                                                onChange={(_, page) => get_filtered_enquiries(page)}
                                                color="primary"
                                                shape="rounded"
                                                showFirstButton
                                                showLastButton
                                            />
                                        </Stack>
                                    </Box>
                                    {viewMode === "cards" ? (
                                        <Grid container spacing={2} className={styles.cardsGrid}>
                                            {displayData.map((enquiry, index) => (
                                                <Grid item xs={12} sm={6} md={4} key={enquiry.id}>
                                                    <Fade in timeout={500} style={{ transitionDelay: `${index * 50}ms` }}>
                                                        <StyledCard onClick={() => handleViewEnquiry(enquiry)}>
                                                            <CardHeader
                                                                avatar={
                                                                    <Avatar sx={{ bgcolor: "primary.main" }}>
                                                                        {enquiry.first_name?.charAt(0) || "N"}
                                                                    </Avatar>
                                                                }
                                                                title={`${enquiry.first_name || "N/A"} ${enquiry.last_name || ""}`}
                                                                subheader={`ID: ${enquiry.id}`}
                                                                action={
                                                                    <Chip
                                                                        icon={getStatusIcon(enquiry.status)}
                                                                        label={enquiry.status || "N/A"}
                                                                        color={getStatusColor(enquiry.status)}
                                                                        size="small"
                                                                        variant="outlined"
                                                                    />
                                                                }
                                                                className={styles.cardHeader}
                                                            />
                                                            <CardContent className={styles.cardContent}>
                                                                {showAssignedCounsellorColumn && (
                                                                    <Typography variant="body2">
                                                                        <strong>Assigned counsellor:</strong>{" "}
                                                                        {enquiry.assigned_counsellor_name || "Unassigned"}
                                                                    </Typography>
                                                                )}
                                                                <Typography variant="body2">
                                                                    <strong>NIC:</strong> {enquiry.nic || "N/A"}
                                                                </Typography>
                                                                <Typography variant="body2">
                                                                    <strong>Age:</strong> {calculateAge(enquiry.date_of_birth)}
                                                                </Typography>
                                                                <Typography variant="body2">
                                                                    <strong>Email:</strong> {enquiry.email || "N/A"}
                                                                </Typography>
                                                                <Typography variant="body2">
                                                                    <strong>Phone:</strong> {enquiry.phone_number || "N/A"}
                                                                </Typography>
                                                                <Typography variant="body2">
                                                                    <strong>Country:</strong> {enquiry.country_of_study || "N/A"}
                                                                </Typography>
                                                                <Typography variant="body2">
                                                                    <strong>Gender:</strong> {enquiry.gender || "N/A"}
                                                                </Typography>
                                                                {role === "admin" && (
                                                                    <Box className={styles.cardActions}>
                                                                        {renderActionButtons(enquiry)}
                                                                    </Box>
                                                                )}
                                                            </CardContent>
                                                        </StyledCard>
                                                    </Fade>
                                                </Grid>
                                            ))}
                                        </Grid>
                                    ) : (
                                        <>
                                            <Paper variant="outlined" className={styles.tableContainer}>
                                                <Table stickyHeader size="small" className={styles.enquiriesTable}>
                                                    <TableHead>
                                                        <TableRow>
                                                            <TableCell className={styles.colId} sortDirection={sortConfig.key === "id" ? sortConfig.direction : false}>
                                                                <TableSortLabel
                                                                    active={sortConfig.key === "id"}
                                                                    direction={sortConfig.key === "id" ? sortConfig.direction : "asc"}
                                                                    onClick={() => requestSort("id")}
                                                                >
                                                                    ID
                                                                </TableSortLabel>
                                                            </TableCell>
                                                            <TableCell className={styles.colName} sortDirection={sortConfig.key === "first_name" ? sortConfig.direction : false}>
                                                                <TableSortLabel
                                                                    active={sortConfig.key === "first_name"}
                                                                    direction={sortConfig.key === "first_name" ? sortConfig.direction : "asc"}
                                                                    onClick={() => requestSort("first_name")}
                                                                >
                                                                    Name
                                                                </TableSortLabel>
                                                            </TableCell>
                                                            <TableCell className={styles.colStatus} sortDirection={sortConfig.key === "status" ? sortConfig.direction : false}>
                                                                <TableSortLabel
                                                                    active={sortConfig.key === "status"}
                                                                    direction={sortConfig.key === "status" ? sortConfig.direction : "asc"}
                                                                    onClick={() => requestSort("status")}
                                                                >
                                                                    Status
                                                                </TableSortLabel>
                                                            </TableCell>
                                                            {showAssignedCounsellorColumn && (
                                                                <TableCell
                                                                    className={styles.colCounsellor}
                                                                    sortDirection={sortConfig.key === "assigned_counsellor_name" ? sortConfig.direction : false}
                                                                >
                                                                    <TableSortLabel
                                                                        active={sortConfig.key === "assigned_counsellor_name"}
                                                                        direction={sortConfig.key === "assigned_counsellor_name" ? sortConfig.direction : "asc"}
                                                                        onClick={() => requestSort("assigned_counsellor_name")}
                                                                    >
                                                                        Assigned counsellor
                                                                    </TableSortLabel>
                                                                </TableCell>
                                                            )}
                                                            <TableCell className={styles.colNic} sortDirection={sortConfig.key === "nic" ? sortConfig.direction : false}>
                                                                <TableSortLabel
                                                                    active={sortConfig.key === "nic"}
                                                                    direction={sortConfig.key === "nic" ? sortConfig.direction : "asc"}
                                                                    onClick={() => requestSort("nic")}
                                                                >
                                                                    NIC
                                                                </TableSortLabel>
                                                            </TableCell>
                                                            <TableCell className={styles.colAge}>Age</TableCell>
                                                            <TableCell className={styles.colEmail} sortDirection={sortConfig.key === "email" ? sortConfig.direction : false}>
                                                                <TableSortLabel
                                                                    active={sortConfig.key === "email"}
                                                                    direction={sortConfig.key === "email" ? sortConfig.direction : "asc"}
                                                                    onClick={() => requestSort("email")}
                                                                >
                                                                    Email
                                                                </TableSortLabel>
                                                            </TableCell>
                                                            <TableCell className={styles.colPhone} sortDirection={sortConfig.key === "phone_number" ? sortConfig.direction : false}>
                                                                <TableSortLabel
                                                                    active={sortConfig.key === "phone_number"}
                                                                    direction={sortConfig.key === "phone_number" ? sortConfig.direction : "asc"}
                                                                    onClick={() => requestSort("phone_number")}
                                                                >
                                                                    Phone
                                                                </TableSortLabel>
                                                            </TableCell>
                                                            {role === "admin" && <TableCell className={styles.colActions}>Actions</TableCell>}
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {displayData.map((enquiry) => (
                                                            <TableRow
                                                                key={enquiry.id}
                                                                hover
                                                                onClick={() => handleViewEnquiry(enquiry)}
                                                                className={styles.tableRow}
                                                            >
                                                                <TableCell className={styles.colId}>{enquiry.id}</TableCell>
                                                                <TableCell className={styles.colName}>
                                                                    <Box className={styles.nameCell}>
                                                                        <Avatar sx={{ width: 28, height: 28, bgcolor: "primary.main", fontSize: 12 }}>
                                                                            {enquiry.first_name?.charAt(0) || "N"}
                                                                        </Avatar>
                                                                        <Box className={styles.nameCellText} component="span">
                                                                            <Typography variant="body2" className={styles.primaryText} noWrap>
                                                                                {enquiry.first_name} {enquiry.last_name}
                                                                            </Typography>
                                                                            <Typography variant="caption" className={styles.secondaryText} noWrap>
                                                                                {enquiry.email}
                                                                            </Typography>
                                                                        </Box>
                                                                    </Box>
                                                                </TableCell>
                                                                <TableCell className={styles.colStatus}>
                                                                    <Chip
                                                                        icon={getStatusIcon(enquiry.status)}
                                                                        label={enquiry.status || "N/A"}
                                                                        color={getStatusColor(enquiry.status)}
                                                                        size="small"
                                                                        variant="outlined"
                                                                    />
                                                                </TableCell>
                                                                {showAssignedCounsellorColumn && (
                                                                    <TableCell className={styles.colCounsellor}>
                                                                        <Typography variant="body2" className={styles.primaryText} noWrap title={enquiry.assigned_counsellor_name || ""}>
                                                                            {enquiry.assigned_counsellor_name || "Unassigned"}
                                                                        </Typography>
                                                                    </TableCell>
                                                                )}
                                                                <TableCell className={styles.colNic}>{enquiry.nic || "N/A"}</TableCell>
                                                                <TableCell className={styles.colAge}>{calculateAge(enquiry.date_of_birth)}</TableCell>
                                                                <TableCell className={styles.colEmail}>
                                                                    <a href={`mailto:${enquiry.email}`} className={styles.emailLink} title={enquiry.email}>
                                                                        {enquiry.email || "N/A"}
                                                                    </a>
                                                                </TableCell>
                                                                <TableCell className={styles.colPhone}>
                                                                    <a href={`tel:${enquiry.phone_number}`} className={styles.phoneLink}>
                                                                        {enquiry.phone_number || "N/A"}
                                                                    </a>
                                                                </TableCell>
                                                                {role === "admin" && (
                                                                    <TableCell className={styles.colActions} onClick={(e) => e.stopPropagation()}>
                                                                        {renderActionButtons(enquiry)}
                                                                    </TableCell>
                                                                )}
                                                            </TableRow>
                                                        ))}
                                                    </TableBody>
                                                </Table>
                                            </Paper>
                                        </>
                                    )}

                                    {/* Pagination - bottom */}
                                    <Box className={styles.pagination}>
                                        <Typography variant="body2" color="text.secondary">
                                            Showing {totalCount === 0 ? 0 : (currentPage - 1) * pageSize + 1}–{Math.min(currentPage * pageSize, totalCount)} of {totalCount} results
                                        </Typography>
                                        <Stack direction="row" alignItems="center" spacing={2}>
                                            <FormControl size="small" sx={{ minWidth: 100 }}>
                                                <InputLabel>Per page</InputLabel>
                                                <Select
                                                    value={pageSize}
                                                    label="Per page"
                                                    onChange={(e) => {
                                                        setPageSize(Number(e.target.value));
                                                        setCurrentPage(1);
                                                    }}
                                                >
                                                    {PAGE_SIZE_OPTIONS.map((n) => (
                                                        <MenuItem key={n} value={n}>{n}</MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                            <Pagination
                                                count={totalPages}
                                                page={currentPage}
                                                onChange={(_, page) => get_filtered_enquiries(page)}
                                                color="primary"
                                                shape="rounded"
                                                showFirstButton
                                                showLastButton
                                            />
                                        </Stack>
                                    </Box>
                                </>
                            )}
                        </Paper>

                {/* Move to Application Form Filled: upload/manual form modal */}
                <ApplicationFormModal
                    open={applicationFormModalOpen}
                    onClose={() => {
                        setApplicationFormModalOpen(false);
                        setApplicationFormEnquiry(null);
                    }}
                    leadId={applicationFormEnquiry?.id}
                    enquiry={applicationFormEnquiry}
                    onSuccess={() => get_filtered_enquiries(currentPage)}
                />

                {/* New Enquiry → Contacted: first comment + Hot/Warm/Cold (same structure as EnquiryComLog) */}
                <Dialog
                    open={contactedModalOpen}
                    onClose={closeContactedModal}
                    maxWidth="sm"
                    fullWidth
                    PaperProps={{ sx: { borderRadius: 2 } }}
                >
                    <DialogTitle sx={{ fontWeight: 600 }}>
                        Mark as Contacted
                    </DialogTitle>
                    <DialogContent>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            This is the first entry into the system. Please add an enquiry comment and select lead temperature.
                        </Typography>
                        {contactedModalEnquiry && (
                            <Typography variant="body2" sx={{ mb: 2 }}>
                                {contactedModalEnquiry.first_name} {contactedModalEnquiry.last_name} (Enquiry #{contactedModalEnquiry.id})
                            </Typography>
                        )}
                        <Box component="form" onSubmit={handleContactedSubmit} noValidate>
                            <FormControl fullWidth size="small" sx={{ mb: 2 }} required>
                                <InputLabel>Lead status</InputLabel>
                                <Select
                                    value={contactLeadStatus}
                                    label="Lead status"
                                    onChange={(e) => setContactLeadStatus(e.target.value)}
                                >
                                    <MenuItem value="">Select Hot / Warm / Cold</MenuItem>
                                    <MenuItem value="Hot">Hot</MenuItem>
                                    <MenuItem value="Warm">Warm</MenuItem>
                                    <MenuItem value="Cold">Cold</MenuItem>
                                </Select>
                            </FormControl>
                            <TextField
                                fullWidth
                                multiline
                                rows={3}
                                label="Enquiry comment"
                                placeholder="Write a comment..."
                                value={contactCommentText}
                                onChange={(e) => setContactCommentText(e.target.value)}
                                required
                                size="small"
                                sx={{ mb: 2 }}
                            />
                            {contactError && (
                                <Typography color="error" variant="body2" sx={{ mb: 2 }}>
                                    {contactError}
                                </Typography>
                            )}
                        </Box>
                    </DialogContent>
                    <DialogActions sx={{ px: 3, pb: 2 }}>
                        <Button onClick={closeContactedModal} disabled={contactSubmitLoading}>
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleContactedSubmit}
                            disabled={contactSubmitLoading || !contactLeadStatus.trim() || !contactCommentText.trim()}
                            startIcon={contactSubmitLoading ? <CircularProgress size={16} color="inherit" /> : null}
                        >
                            {contactSubmitLoading ? "Saving…" : "Add comment & mark Contacted"}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Submit for visa: select application modal */}
                <Dialog
                    open={submitVisaModalOpen}
                    onClose={closeSubmitVisaModal}
                    maxWidth="sm"
                    fullWidth
                    PaperProps={{ sx: { borderRadius: 2 } }}
                >
                    <DialogTitle sx={{ fontWeight: 600 }}>
                        Submit for visa – select application
                    </DialogTitle>
                    <DialogContent>
                        {submitVisaEnquiry && (
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                {submitVisaEnquiry.first_name} {submitVisaEnquiry.last_name} (Enquiry #{submitVisaEnquiry.id})
                            </Typography>
                        )}
                        {submitVisaFetching ? (
                            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                                <CircularProgress size={32} />
                            </Box>
                        ) : submitVisaApplications.length === 0 ? (
                            <Typography color="text.secondary">No applications found for this student.</Typography>
                        ) : (
                            <List dense disablePadding>
                                {submitVisaApplications.map((app) => (
                                    <ListItemButton
                                        key={app.id}
                                        selected={submitVisaSelectedId === app.id}
                                        onClick={() => setSubmitVisaSelectedId(app.id)}
                                        sx={{ borderRadius: 1, mb: 0.5 }}
                                    >
                                        <Radio
                                            size="small"
                                            checked={submitVisaSelectedId === app.id}
                                            onChange={() => setSubmitVisaSelectedId(app.id)}
                                            onClick={(e) => e.stopPropagation()}
                                            sx={{ mr: 1 }}
                                        />
                                        <ListItemText
                                            primary={app.institution || app.course_name || `Application #${app.id}`}
                                            secondary={app.course_name ? `${app.course_name}${app.status ? ` · ${app.status}` : ""}` : (app.status || null)}
                                        />
                                    </ListItemButton>
                                ))}
                            </List>
                        )}
                    </DialogContent>
                    <DialogActions sx={{ px: 3, pb: 2 }}>
                        <Button onClick={closeSubmitVisaModal} color="inherit">
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleSubmitVisaSave}
                            disabled={submitVisaLoading || submitVisaFetching || !submitVisaSelectedId || submitVisaApplications.length === 0}
                            startIcon={submitVisaLoading ? <CircularProgress size={18} color="inherit" /> : <SendIcon />}
                        >
                            {submitVisaLoading ? "Saving…" : "Save & submit for visa"}
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
        </ThemeProvider>
    );
}

export default AllEnquiries;